import {
  Box,
  Center,
  Flex,
  Heading,
  Image,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
} from "@chakra-ui/react";
import { type TezosToolkit } from "@taquito/taquito";
import {
  type MultichainTokenBalance,
  type SignTransactionOptions,
  useChainRegistry,
} from "@umami/chains";
import {
  type UnifiedBalanceWithPrices,
  useCurrentAccount,
  useGetNetworkChain,
  useSelectedNetwork,
} from "@umami/state";
import {
  BridgeDirection,
  type GaslessQuoteResponse,
  type LiFiQuoteResponse,
  type SwapExecutionParams,
  SwapExecutionService,
  type ZeroXQuoteResponse,
} from "@umami/swap";
import { CustomError } from "@umami/utils";
import { type TransactionResponse } from "ethers";
import { FormProvider, useForm } from "react-hook-form";

import { type QuoteWithProvider } from "./providers";
import { ModalBackButton } from "../BackButton";
import { SwapInfo } from "./SwapInfo";
import { ChainIcon } from "../Token/ChainIcon";

import { SignButton } from "@/components/SendFlow/SignButton";
import { useBridge } from "@/hooks/useBridge";
import { useTezosBridge } from "@/hooks/useTezosBridge";
import { useColor } from "@/styles/useColor";

const TokenDisplay = ({ token }: { token: UnifiedBalanceWithPrices }) => {
  const tokenWithIcon = token as UnifiedBalanceWithPrices & { iconUrl?: string };

  const getNetworkChain = useGetNetworkChain();

  return tokenWithIcon.iconUrl ? (
    <Image
      width="24px"
      minWidth="24px"
      alt={token.symbol}
      fallbackSrc="./static/coin-front.svg"
      rounded="full"
      src={tokenWithIcon.iconUrl}
    />
  ) : (
    <ChainIcon boxSize="24px" chainId={getNetworkChain(token.chainId)} />
  );
};

interface SwapConfirmationProps {
  onConfirm: () => Promise<void>;
  sellToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
  buyToken: UnifiedBalanceWithPrices & MultichainTokenBalance;
  sellAmount: string;
  buyAmount: string;
  sellFiatAmount: string;
  buyFiatAmount: string;
  minimumReceived: string;
  conversionRate: number;
  fiatValue: string;
  priceImpact: number;
  slippage: number;
  direction: BridgeDirection;
  zeroXQuote?: ZeroXQuoteResponse | null;
  gaslessQuote?: GaslessQuoteResponse | null;
  lifiQuote?: LiFiQuoteResponse | null;
  selectedQuote?: QuoteWithProvider | null;
  isGaslessSwap?: boolean;
  isFastWithdrawal?: boolean;
}

export const ConfirmSwapModal = ({
  onConfirm,
  sellToken,
  buyToken,
  sellAmount,
  buyAmount,
  sellFiatAmount,
  buyFiatAmount,
  minimumReceived,
  conversionRate,
  fiatValue,
  priceImpact,
  slippage,
  direction,
  zeroXQuote,
  gaslessQuote,
  lifiQuote,
  selectedQuote,
  isGaslessSwap = false,
  isFastWithdrawal = false,
}: SwapConfirmationProps) => {
  const color = useColor();
  const form = useForm();
  const currentAccount = useCurrentAccount();
  const { prepareApproveToken, prepareBridgeToken, createSigner, bridge } = useBridge();
  const { bridge: tezosBridge } = useTezosBridge();
  const { registry } = useChainRegistry();
  const currentNetwork = useSelectedNetwork();
  const getNetworkChain = useGetNetworkChain();

  if (!currentAccount) {
    throw new CustomError("No account selected");
  }

  const isTezosEtherlinkSwap =
    direction === BridgeDirection.TezosToEtherlink ||
    direction === BridgeDirection.EtherlinkToTezos;

  const handleConfirm = async (
    tezosToolkit: TezosToolkit,
    options: SignTransactionOptions
  ): Promise<TransactionResponse | void> => {
    try {
      const executionParams: SwapExecutionParams = {
        sellToken,
        buyToken,
        sellAmount,
        buyAmount,
        direction,
        selectedQuote,
        zeroXQuote,
        gaslessQuote,
        lifiQuote,
        isGaslessSwap,
        currentNetwork,
        currentAccount,
        tezosToolkit,
        options,
        isFastWithdrawal,
        bridge: {
          ethereumProvider: bridge.ethereumProvider,
          getErc20Contract: bridge.getErc20Contract,
          getProvider: bridge.getProvider,
        },
        tezosBridge,
        createSigner,
        prepareApproveToken,
        prepareBridgeToken,
        registry,
        onConfirm,
        getNetworkChain,
      };

      return await SwapExecutionService.executeSwap(executionParams);
    } catch (error) {
      if (error instanceof Error) {
        throw new CustomError(error.message);
      }
      throw new CustomError("Swap execution failed");
    }
  };

  return (
    <FormProvider {...form}>
      <ModalContent>
        <ModalHeader>
          <ModalBackButton payload={{ buyAmount: undefined }} />
          <ModalCloseButton />
          <Center flexDirection="column" gap="12px">
            <Heading size="xl">Confirm Swap</Heading>
            {isGaslessSwap && (
              <Text color={color("500")} fontSize="sm">
                Gasless swap - no gas fees required
              </Text>
            )}
          </Center>
        </ModalHeader>
        <ModalBody>
          <Box>
            <Flex justifyContent="space-between" marginBottom="3">
              <Text color={color("900")} fontSize="lg" fontWeight="600">
                Selling
              </Text>
              <Text color={color("500")} fontSize="md">
                Network:{" "}
                <Text as="span" fontWeight="bold" textTransform="capitalize">
                  {getNetworkChain(sellToken.chainId)}
                </Text>
              </Text>
            </Flex>

            <Flex
              alignItems="center"
              gap="4"
              marginBottom="6"
              padding="16px"
              background={color("100")}
              borderRadius="8px"
            >
              <TokenDisplay token={sellToken} />
              <Box>
                <Text color={color("900")}>
                  <Text as="span" fontWeight="600">
                    {sellAmount}
                  </Text>{" "}
                  <Text as="span" fontWeight="400">
                    {sellToken.symbol}
                  </Text>
                </Text>
                <Text color={color("500")} fontSize="sm">
                  {sellFiatAmount}
                </Text>
              </Box>
            </Flex>

            <Flex justifyContent="space-between" marginBottom="3">
              <Text color={color("900")} fontSize="lg" fontWeight="600">
                Receiving
              </Text>
              <Text color={color("500")} fontSize="md">
                Network:{" "}
                <Text as="span" fontWeight="bold" textTransform="capitalize">
                  {getNetworkChain(buyToken.chainId)}
                </Text>
              </Text>
            </Flex>

            <Flex
              alignItems="center"
              gap="4"
              marginBottom="6"
              padding="16px"
              background={color("100")}
              borderRadius="8px"
            >
              <TokenDisplay token={buyToken} />
              <Box>
                <Text color={color("900")}>
                  <Text as="span" fontWeight="600">
                    {buyAmount}
                  </Text>{" "}
                  <Text as="span" fontWeight="400">
                    {buyToken.symbol}
                  </Text>
                </Text>
                <Text color={color("500")} fontSize="sm">
                  {buyFiatAmount}
                </Text>
              </Box>
            </Flex>
            <SwapInfo
              direction={direction}
              buyToken={buyToken}
              buyTokenSymbol={buyToken.symbol}
              conversionRate={conversionRate}
              defaultSlippage={slippage}
              fiatValue={fiatValue}
              lifiQuote={lifiQuote}
              minimumReceived={minimumReceived}
              onSlippageChange={() => {}}
              priceImpact={priceImpact}
              provider={selectedQuote?.provider}
              sellToken={sellToken}
              sellTokenSymbol={sellToken.symbol}
              // We are swapping tokens on high liquidity pools,
              // 0x suggest to not tweak slippage and price impact in this case
              showConversionRate={false}
              showNetworkFee={!isTezosEtherlinkSwap && !isGaslessSwap}
              showPriceImpactAndSlippage={false}
              showSlippageControls={false}
              zeroXQuote={zeroXQuote}
            />
          </Box>
        </ModalBody>
        <ModalFooter>
          <SignButton onSubmit={handleConfirm} signer={currentAccount} text="Confirm" />
        </ModalFooter>
      </ModalContent>
    </FormProvider>
  );
};
